"use client";
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom/client";
import MainScreen from "./components/MainScreen/MainScreen";
import { trackAddToCart } from "./api/trackAddToCart";
import { Buffer } from 'buffer';
global.Buffer = Buffer;

// Global initialization tracking
let isInitialized = false;
let initializedUrl = "";
let isInitializing = false;
let currentSessionId = null;

const generateSessionId = () => crypto.randomUUID();
const GLOBAL_SESSION_ID = generateSessionId();
console.log("Created initial session ID:", GLOBAL_SESSION_ID);

if (typeof window !== "undefined") {
  window.qaWidgetSessionId = GLOBAL_SESSION_ID;
  currentSessionId = GLOBAL_SESSION_ID;
}

// Track processed event IDs
const processedEventIds = new Set();

const handleTrackEvent = (eventType, eventData) => {
  if (eventData.eventId && processedEventIds.has(eventData.eventId)) {
    console.log(`Skipping duplicate event: ${eventData.eventId}`);
    return;
  }
  if (eventData.eventId) {
    processedEventIds.add(eventData.eventId);
  }
  const sessionId = eventData.sessionId || currentSessionId || GLOBAL_SESSION_ID;
  
  console.log(`Event tracked: ${eventType}`, eventData);
  
  switch(eventType) {
    case 'addToCart':
      if (!window.qaWidgetConfig?.trackAddToCart) {
        console.log('Add to cart tracking is disabled');
        return;
      }
      console.log('Add to Cart event detected!', eventData);
      trackAddToCart(
        eventData.url, 
        eventData.productNumber,
        sessionId,
        eventData.customerId,
        eventData.website || window.location.origin,
        eventData.userId,
      );
      break;
    default:
      console.log('Unknown event type:', eventType);
  }
};

const processPendingEvents = () => {
  const pendingCount = window.qaWidgetPendingEvents?.length || 0;
  console.log(`Checking for pending events. Found: ${pendingCount}`);
  if (pendingCount > 0) {
    console.log(`Processing ${pendingCount} pending events`);
    window.qaWidgetPendingEvents.forEach(event => {
      handleTrackEvent(event.type, event.data);
    });
    window.qaWidgetPendingEvents = [];
  }
};

const QAWidget = () => {
  const sessionIdRef = useRef(generateSessionId());
  console.log("Widget initialized with new session ID:", sessionIdRef.current);
  
  useEffect(() => {
    currentSessionId = sessionIdRef.current;
    window.qaWidgetSessionId = currentSessionId;
    console.log("Updated current session ID:", currentSessionId);
  }, []);

  const [config, setConfig] = useState({
    currentUrl: window.qaWidgetConfig?.currentUrl || window.location.href,
    customerId: window.qaWidgetConfig?.customerId || '',
    website: window.qaWidgetConfig?.website || window.location.origin,
    productNumber: window.qaWidgetConfig?.productNumber || '',
    userId: window.qaWidgetConfig?.userId || ''
  });

  // Updated handlePlacement: update global config and reposition the widget container if needed.
  const handlePlacement = (placement) => {
    console.log("Custom widget placement from backend:", placement);
    window.qaWidgetConfig = window.qaWidgetConfig || {};
    window.qaWidgetConfig.placement = placement;
    // Reposition container if it already exists
    const container = document.getElementById("truste-qa-widget-container");
    if (container) {
      let referenceSelector = null;
      let marginTop = null;
      if (config.customerId === 'f96b6d68-33d3-44cc-ab16-2b31f4949b64') {
        referenceSelector = 'section#product-tabs';
      } else if (config.customerId === 'ef746eab-2a8c-4944-ac35-76a5490600e8') {
        referenceSelector = 'div.additional-information';
        marginTop = '30px';
      }

      if (referenceSelector) {
        const reference = document.querySelector(referenceSelector);
        if (reference && placement === "under_description") {
          reference.insertAdjacentElement('afterend', container);
          if (marginTop) {
            container.style.marginTop = marginTop;
          }
          console.log(`Repositioned widget container after reference element (${referenceSelector}) due to placement update:`, placement);
        } else {
          console.log(`No reposition done. Either reference element (${referenceSelector}) not found or placement is not 'under_description'.`);
        }
      } else {
         console.log(`No specific selector defined for customerId: ${config.customerId}`);
      }
    } else {
      console.log("Widget container not yet created; reposition will occur upon creation.");
    }
  };

  useEffect(() => {
    const updateHandler = (e) => {
      if (e.detail?.url) {
        setConfig(prev => ({
          ...prev,
          currentUrl: e.detail.url,
          productNumber: e.detail.productNumber || prev.productNumber
        }));
      }
    };
    window.addEventListener('qaWidgetUrlUpdate', updateHandler);
    return () => window.removeEventListener('qaWidgetUrlUpdate', updateHandler);
  }, []);

  useEffect(() => {
    if (!window.qaWidgetTrackEvent) {
      console.log("Setting up qaWidgetTrackEvent handler");
      window.qaWidgetTrackEvent = handleTrackEvent;
    }
    processPendingEvents();
    return () => {
      if (window.qaWidgetTrackEvent === handleTrackEvent) {
        delete window.qaWidgetTrackEvent;
      }
    };
  }, []);

  useEffect(() => {
    if (window.qaWidgetConfig?.themeConfig) {
      const root = document.documentElement;
      Object.entries(window.qaWidgetConfig.themeConfig).forEach(([key, value]) => {
        root.style.setProperty(`--qa-${key}`, value);
      });
    }
  }, []);

  return (
    <div className="qa-widget-container" style={{ height: "100%", width: "100%", backgroundColor: "transparent"}}>
      <MainScreen 
        productLink={config.currentUrl}
        sessionId={sessionIdRef.current}
        customerId={config.customerId}
        website={config.website}
        productNumber={config.productNumber}
        userId={config.userId}
        handlePlacement={handlePlacement}
      />
    </div>
  );
};

const initializeQAWidget = () => {
  const url = window.qaWidgetConfig?.currentUrl || window.location.href;
  if ((isInitialized && initializedUrl === url) || isInitializing) {
    console.log("Widget already initialized or initializing for this URL");
    return;
  }
  console.log("Initializing widget for URL:", url);
  isInitializing = true;
  try {
    const container = document.getElementById("truste-qa-widget-container");
    if (!container) {
      console.error("Widget container not found");
      isInitializing = false;
      return;
    }
    container.setAttribute('data-initialized', 'true');
    const root = ReactDOM.createRoot(container);
    root.render(<QAWidget />);
    isInitialized = true;
    initializedUrl = url;
    processPendingEvents();
  } catch (error) {
    console.error("Error initializing widget:", error);
  } finally {
    isInitializing = false;
  }
};

const updateQAWidgetUrl = (newUrl, productNumber) => {
  if (!newUrl) return;
  console.log("Updating widget URL to:", newUrl);
  window.dispatchEvent(new CustomEvent('qaWidgetUrlUpdate', { detail: { url: newUrl, productNumber } }));
};

if (typeof window !== "undefined") {
  window.initializeQAWidget = initializeQAWidget;
  window.updateQAWidgetUrl = updateQAWidgetUrl;
}

export { QAWidget };
